<template>
  <div style="margin: auto;" class="pt-3">
    <v-flex
      xs12
      lg10
      xl8
      style="margin: auto;"
      class="pb-5 px-2"
    >

      <v-card>
        <v-card-title primary-title>
          <h3 v-if="isAdmin" class="headline">
            <span>
              {{ $t('usersBalancesContainer.totalCityBalance', { city: currentlyLoggedUserCity, amount: totalCityBalance.toFixed(2) }) }}&euro;
            </span>
          </h3>
          <h3 v-else class="headline">
            <span>
              {{ $t('usersBalancesContainer.balances') }}
            </span>
          </h3>
          <v-spacer />
          <v-text-field
            v-if="isAdmin"
            v-model="searchQueryComputed"
            append-icon="search"
            :label="$t('common.search')"
            single-line
            hide-details
          />
        </v-card-title>
        <dialog-wrapper :visible="newDepositDialog">
          <v-dialog v-model="newDepositDialog" max-width="500" persistent>
            <new-cash-entry-modal
              ref="modalForm"
              :info-field-value="newDepositUser.name"
              @close-dialog="onNewDepositModalClose"
              @new-cash-entry-confirmed="onDepositConfirmed"
            />
          </v-dialog>
        </dialog-wrapper>
        <v-data-table
          ref="usersBalancesDataTable"
          :rows-per-page-text="$t('dataTables.rowsPerPage')"
          :rows-per-page-items="rowsPerPageOptions"
          :no-data-text="$t('dataTables.noData')"
          :headers="usersBalancesColumnHeaders"
          :items="mappedUsers"
          :loading="$apollo.loading"
          :pagination.sync="paginationBalances"
          :total-items="usersWithPagingData.pagingData ? usersWithPagingData.pagingData.totalResultsCount : 0"
          :hide-actions="!isAdmin"
        >
          <template slot="items" slot-scope="props">
            <tr @click="props.expanded = !props.expanded; selectUser(props.item.id)">
              <td class="text-xs-left">
                <div>
                  {{ props.item.displayName }}
                  <span class="grey--text">
                    ({{ props.item.email }})
                  </span>
                </div>
              </td>
              <td class="text-xs-right">
                <div>
                  {{ props.item.balance.toFixed(2) }}
                  <span class="grey--text">
                    &euro;
                  </span>
                </div>
              </td>
              <td v-if="isAdmin" class="text-xs-right">
                <v-btn
                  style="float: right; margin-right: -7px;"
                  color="primary"
                  @click.native.stop
                  @click="openNewDepositModal(props.item.id, props.item.displayName)"
                >
                  {{ $t('usersBalancesContainer.newDeposit') }}
                </v-btn>
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="{}">
            <v-card v-if="userDepositsLoaded" style="padding: 4px 16px">
              <v-data-table
                :rows-per-page-text="$t('dataTables.rowsPerPage')"
                :rows-per-page-items="rowsPerPageOptions"
                :no-data-text="$t('dataTables.noData')"
                :headers="userDepositsColumnHeaders"
                :items="mappedUserDeposits"
                :pagination.sync="paginationDeposits"
                :total-items="userTransactionsWithPagingData.pagingData ? userTransactionsWithPagingData.pagingData.totalResultsCount : 0"
              >
                <template slot="items" slot-scope="props">
                  <tr>
                    <td class="text-xs-left">{{ props.item.updatedAt }}</td>
                    <td class="text-xs-right">
                      {{ props.item.amount.toFixed(2) }}
                      <span class="grey--text">
                        &euro;
                      </span>
                    </td>
                    <td class="text-xs-left">
                      <div>
                        {{ props.item.depositedByUserName }}
                        <span class="grey--text">
                          ({{ props.item.depositedByEmail }})
                        </span>
                      </div>
                    </td>
                    <td class="text-xs-left textwrap">{{ props.item.comment }}</td>
                  </tr>
                </template>
                <template slot="pageText" slot-scope="props">
                  {{ $t('dataTables.pagingRow', { start: props.pageStart, stop: props.pageStop, length: props.itemsLength }) }}
                </template>
              </v-data-table>
            </v-card>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ $t('dataTables.pagingRow', { start: props.pageStart, stop: props.pageStop, length: props.itemsLength }) }}
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
  </div>
</template>

<script>
import moment from 'moment';
import gql from 'graphql-tag';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import NewCashEntryModal from '../common/new-cash-entry-modal/NewCashEntryModal.vue';
import AuthenticationService from '@/services/AuthenticationService';
import TimeService from '@/services/TimeService';
import { apolloClient } from '@/apollo';
import DialogWrapper from '../common/dialog-wrapper/DialogWrapper.vue';

const defaultPaginationBalances = {
  sortBy: 'displayName',
  descending: false,
  page: 1,
  rowsPerPage: 10,
};

export default {
  name: 'UsersBalancesContainer',
  components: { NewCashEntryModal, DialogWrapper },
  data() {
    return {
      searchQuery: '',
      usersWithPagingData: {
        users: [],
        pagingData: {
          totalResultsCount: 0,
        },
        totalCityBalance: 0,
      },
      userTransactionsWithPagingData: {
        transactions: [],
        pagingData: {
          totalResultsCount: 0,
        },
      },
      selectedUserId: undefined,
      currentNewDeposit: undefined,
      userDepositsLoaded: false,
      newDepositDialog: false,
      depositConfirmationDialog: false,
      newDepositUser: {
        id: undefined,
        name: undefined,
      },

      paginationBalances: defaultPaginationBalances,
      paginationDeposits: {
        sortBy: 'updatedAt',
        descending: true,
        page: 1,
        rowsPerPage: 10,
      },
    };
  },
  computed: {
    ...mapGetters('userInfoStore', ['userData', 'cities']),
    currentlyLoggedUserCity() {
      return this.userData.cityId ? `${this.cities.find(x => x.id === this.userData.cityId).name}` : '';
    },
    usersBalancesColumnHeaders() {
      const headers = [
        {
          text: this.$t('usersBalancesContainer.name'),
          value: 'displayName',
          align: 'left',
          sortable: this.isAdmin,
        },
        {
          text: this.$t('usersBalancesContainer.balance'),
          value: 'balance',
          align: 'right',
          sortable: true,
        },
        {
          align: 'right',
          sortable: false,
          value: 'action',
        },
      ];

      if (this.isAdmin) return headers;

      return headers.filter(h => h.value !== 'action');
    },
    userDepositsColumnHeaders() {
      const headers = [
        {
          text: this.$t('usersBalancesContainer.createdAt', { tzOffset: TimeService.getCurrentTimeZone() }),
          value: 'createdAt',
          align: 'left',
          sortable: true,
        },
        {
          text: this.$t('common.amount'),
          value: 'amount',
          align: 'right',
          sortable: false,
        },
        {
          text: this.$t('usersBalancesContainer.authoredBy'),
          value: 'depositedByUserName',
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('common.comment'),
          value: 'comment',
          align: 'left',
          sortable: false,
        },
      ];

      return headers;
    },
    mappedUsers() {
      return this.usersWithPagingData.users.map(u => ({
        id: u.id,
        displayName: u.displayName,
        email: u.email,
        balance: u.balance,
      }));
    },
    mappedUserDeposits() {
      return this.userTransactionsWithPagingData.transactions.map(d => ({
        amount: d.amount,
        comment: d.comment,
        updatedAt: moment(d.updatedAt).format(TimeService.dateTimeFormat),
        depositedByUserName: d.depositedByUser.displayName,
        depositedByEmail: d.depositedByUser.email,
      }));
    },
    totalCityBalance() {
      return this.usersWithPagingData.totalCityBalance;
    },
    searchQueryComputed: {
      get() {
        return this.searchQuery;
      },
      set: _.debounce(function setSearchQuery(newValue) {
        this.searchQuery = newValue;
      }, 500),
    },
    isAdmin() {
      return AuthenticationService.isAdmin();
    },
    rowsPerPageOptions() {
      return [10, 20, 30];
    },
  },
  methods: {
    selectUser(id) {
      if (this.selectedUserId !== id) {
        this.paginationDeposits.page = 1;
        this.selectedUserId = id;
      }
    },
    openNewDepositModal(id, name) {
      this.newDepositDialog = true;
      this.newDepositUser.id = id;
      this.newDepositUser.name = name;
    },
    onDepositConfirmed(newDeposit) {
      this.$refs.modalForm.newCashEntry.loading = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation NewDepositMutation($data: DepositInput!) {
              newDeposit(data: $data)
            }
          `,
          variables: {
            data: {
              depositedForUserId: this.newDepositUser.id,
              amount: newDeposit.amount,
              comment: newDeposit.comment,
            },
          },
        })
        .then(() => {
          if (this.newDepositUser.name === this.userData.displayName) {
            apolloClient.queryManager.refetchQueryByName('CurrentUserQuery');
          }
          const isExpanded = this.$refs.usersBalancesDataTable.expanded[this.newDepositUser.id];
          this.onNewDepositModalClose();
          if (isExpanded) {
            this.$apollo.queries.userTransactionsWithPagingData.refetch();
          }
          this.$apollo.queries.usersWithPagingData.refetch();
        })
        .catch((error) => {
          ErrorHandler.handleApolloError(error);
          this.$refs.modalForm.newCashEntry.loading = false;
        });
    },
    onNewDepositModalClose() {
      this.newDepositDialog = false;
      this.depositConfirmationDialog = false;
      this.currentNewDeposit = undefined;
      this.$refs.modalForm.formInputReset();
      this.newDepositUser.id = undefined;
      this.newDepositUser.name = undefined;
    },
  },
  apollo: {
    usersWithPagingData: {
      fetchPolicy: 'network-only',
      query: gql`
        query UsersWithPagingDataQuery($itemsPerPage: Int!, $offset: Int!, $sortField: String, $sortDirection: String, $searchQuery: String) {
          usersWithPagingData(itemsPerPage: $itemsPerPage, offset: $offset, sortField: $sortField, sortDirection: $sortDirection, searchQuery: $searchQuery) {
            users {
              id
              displayName
              email
              balance
            }
            pagingData {
              totalResultsCount
              resultsInPage
            }
            totalCityBalance
          }
        }
      `,
      variables() {
        return {
          itemsPerPage: this.paginationBalances.rowsPerPage,
          offset: (this.paginationBalances.page - 1) * this.paginationBalances.rowsPerPage,
          sortField: this.paginationBalances.sortBy,
          sortDirection: this.paginationBalances.descending ? 'DESC' : 'ASC',
          searchQuery: this.searchQueryComputed,
        };
      },
      skip() {
        return _.isEqual(this.paginationBalances, defaultPaginationBalances);
      },
      error(error) {
        this.usersWithPagingData.users = [];
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();

          if (!this.isAdmin) {
            this.$refs.usersBalancesDataTable.expanded = { [r.data.usersWithPagingData.users[0].id]: true };
            this.selectUser(r.data.usersWithPagingData.users[0].id);
          }
        }
      },
    },
    userTransactionsWithPagingData: {
      fetchPolicy: 'network-only',
      query: gql`
        query UserTransactionsWithPagingDataQuery($depositedForUserId: Int!, $itemsPerPage: Int!, $offset: Int!, $sortField: String, $sortDirection: String) {
          userTransactionsWithPagingData(
            depositedForUserId: $depositedForUserId
            itemsPerPage: $itemsPerPage
            offset: $offset
            sortField: $sortField
            sortDirection: $sortDirection
          ) {
            transactions {
              amount
              comment
              updatedAt
              depositedByUser {
                displayName
                email
              }
            }
            pagingData {
              totalResultsCount
              resultsInPage
            }
          }
        }
      `,
      variables() {
        return {
          depositedForUserId: this.selectedUserId,
          itemsPerPage: this.paginationDeposits.rowsPerPage,
          offset: (this.paginationDeposits.page - 1) * this.paginationDeposits.rowsPerPage,
          sortField: this.paginationDeposits.sortBy,
          sortDirection: this.paginationDeposits.descending ? 'DESC' : 'ASC',
        };
      },
      skip() {
        return !this.selectedUserId;
      },
      error(error) {
        this.userTransactionsWithPagingData.transactions = [];
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
        this.userDepositsLoaded = true;
      },
    },
  },
};
</script>
<style scoped>
.textwrap {
  max-width: 300px;
  overflow-wrap: break-word;
}
</style>
